import { Button } from "@svea/design-system-components-react";
import { ButtonGroup, P, Stack, Text, useSigning, useText } from "@sveabank/cc-common";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import IconWithText from "../../components/icon-with-text";
import PackageSignBox from "../../components/sign-box/package-signbox";
import { usePageContext } from "../../hooks/context";
import { useLazyFetchPackageAgreementQuery } from "../../services/package-agreement";
import {
	useCancelSignMutation,
	useGetSigningStatusMutation,
	useInitializePackageSigningMutation,
} from "../../services/signing";
import { setError } from "../../slices/errorSlice";
import type { AssignEmailFormValues, SignatorEmailAssignPost, Signer } from "../../types/signing";
import { normalizeError } from "../../utils/error-handling";
import AddEmailModal from "./add-signator-email-modal";
import AgreementSigningModal from "./agreement-signing";
import { checkSignatorSigningStatus } from "./helpers";
import { useSignInformation } from "./hooks/hooks";
import PackageSuccessModal from "./package-success-modal";

type Props = {
	sendEmailButton?: boolean;
};

const CustomerAgreementSection = ({ sendEmailButton = true }: Props) => {
	const [t, texts] = useText();
	const dispatch = useDispatch();

	const [isAgreementModalOpen, setIsAgreementModalOpen] = useState(false);
	const [isAddSignerEmailModalOpen, setIsAddSignerEmailModalOpen] = useState(false);
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	const {
		refetchSteps,
		applicationId,
		isSignPackageCompleted,
		assignSignatorEmails,
		packageSignStatuses,
		applicantContactInfo,
		setupAgreements,
		signatories,
		areStepsCompleted,
		hasCreditProduct,
		isSetupGuarantorAgreementLoading,
		isSetupPackageAgreementLoading,
		applicationDetails,
		hasCurrentUserSignedPackage,
		currentUserNationalId,
		setAgreementStepHasModalOpen,
	} = usePageContext(useSignInformation);

	const [
		fetchPackageAgreement,
		{ data: packageAgreement, isFetching: isFetchingPackageAgreement },
	] = useLazyFetchPackageAgreementQuery();
	const { requestWithProductSigning } = useSigning();

	const [initPackageSigningRequest] = useInitializePackageSigningMutation();
	const [getSigningStatus] = useGetSigningStatusMutation();
	const [cancelSign] = useCancelSignMutation();

	const signPackageLoader =
		isFetchingPackageAgreement ||
		isSetupGuarantorAgreementLoading ||
		isSetupPackageAgreementLoading;

	const handleOpenSignModal = async () => {
		if (!areStepsCompleted("SetupPackageAgreementStep")) {
			await setupAgreements();
		}
		await fetchPackageAgreement(applicationId).unwrap();
		setIsAgreementModalOpen(true);
	};

	useEffect(() => {
		setAgreementStepHasModalOpen(isAgreementModalOpen || isSuccessModalOpen);
	}, [isAgreementModalOpen, isSuccessModalOpen]);

	const handleSigning = async () => {
		const init = () => initPackageSigningRequest({ applicationId }).unwrap();
		const status = () => getSigningStatus({ applicationId }).unwrap();
		const cancel = () => cancelSign(applicationId).unwrap();

		try {
			const request = requestWithProductSigning(init, status, cancel);

			await request(applicationId);

			await refetchSteps().unwrap();
			setIsAgreementModalOpen(false);
			setIsSuccessModalOpen(true);
		} catch (error) {
			if (error instanceof Error) {
				if (error.message === "BankIdTimeout") {
					dispatch(setError(normalizeError(error)));
					return;
				}
			}
			throw error;
		}
	};

	const handleSendEmails = async (formValues: AssignEmailFormValues) => {
		const nonNullEmailSignersArray = formValues.alteredSigners.filter(
			(signer) => typeof signer.email === "string" && signer.email.length > 0,
		);
		const assignSignatorEmailArray: SignatorEmailAssignPost[] = nonNullEmailSignersArray.map(
			(signer) => ({
				email: signer.email,
				nationalId: signer.nationalId,
			}),
		);
		setIsAddSignerEmailModalOpen(false);
		await assignSignatorEmails({
			applicationId,
			signatories: assignSignatorEmailArray,
		});
		await refetchSteps().unwrap();
	};

	return (
		<Stack gap={4}>
			<P>
				{t(texts.signPage.package.descriptionPrimary)}
				<Text bold>{t(texts.signPage.package.descriptionBold)}</Text>
				{t(texts.signPage.package.descriptionSecondary)}
			</P>
			{signatories.length < 2 && <P>{t(texts.signPage.package.singleSigner)}</P>}
			<P>
				<Text bold>{t(texts.signPage.package.signInstructionSubtitle)}</Text>
			</P>
			<P>{applicationDetails.organization.signatoriesInfo}</P>

			{signatories.map((item: Signer) => (
				<PackageSignBox
					key={item.nationalId}
					signer={item}
					isSigned={checkSignatorSigningStatus(item, packageSignStatuses)}
					signable={item.nationalId === currentUserNationalId}
					handleOpenSignModal={handleOpenSignModal}
					isFetchingAgreement={signPackageLoader}
					isSignStepCompleted={isSignPackageCompleted}
				/>
			))}

			{hasCurrentUserSignedPackage && !isSignPackageCompleted && hasCreditProduct && (
				<IconWithText
					text={t(texts.signPage.package.needMorePackageSignaturesWarning)}
					imageSrc="WARNING_SIGN"
				/>
			)}

			{packageSignStatuses?.length > 1 && sendEmailButton && (
				<ButtonGroup row>
					<Button
						variant="primary"
						type="button"
						onPress={() => {
							setIsAddSignerEmailModalOpen(true);
						}}
					>
						{t(texts.signPage.assignEmailModal.sendButton)}
					</Button>
				</ButtonGroup>
			)}

			<PackageSuccessModal
				isOpen={isSuccessModalOpen}
				onClose={() => {
					setIsSuccessModalOpen(false);
				}}
			/>

			<AddEmailModal
				signers={signatories}
				setOpen={setIsAddSignerEmailModalOpen}
				handleSend={handleSendEmails}
				isOpen={isAddSignerEmailModalOpen}
				applicantId={applicationDetails.applicant.nationalId}
				applicantEmail={applicantContactInfo.email}
			/>
			{packageAgreement && (
				<AgreementSigningModal
					isModalOpen={isAgreementModalOpen}
					setOpen={setIsAgreementModalOpen}
					title={t(texts.signPage.package.packageSigningTitle)}
					signAction={handleSigning}
					isSigning={false}
					pdfData={packageAgreement}
				/>
			)}
		</Stack>
	);
};

export default CustomerAgreementSection;
